import { useEffect, useMemo, useRef, useState, type ComponentType } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { changeConsolePassword } from "../api/authApi";
import {
  IconBriefcase,
  IconChevronDown,
  IconClose,
  IconFolder,
  IconHome,
  IconLayers,
  IconMenu,
} from "./Icons";
import { useAuth } from "../context/AuthContext";
import { postClientAuditEvent } from "../api/auditLogApi";
import { installClientActionAudit } from "../utils/clientAudit";
import { AppFooter } from "./AppFooter";
import { canAccessApplicationsHub } from "../config/applicationHubPermissions";
import { ShellNavLinks } from "./ShellNavLinks";

const indirammaLogoSrc = new URL("../assets/indiramma_indlu_logo1.png", import.meta.url).href;

function isApplicationsHubPath(pathname: string) {
  return (
    pathname === "/applications" ||
    pathname.startsWith("/applications/") ||
    pathname === "/leads" ||
    pathname.startsWith("/leads/")
  );
}

function isProjectsPath(pathname: string) {
  return (
    pathname === "/subcontractor-to-beneficiary" ||
    pathname.startsWith("/subcontractor-to-beneficiary/") ||
    pathname === "/timeline-approvals" ||
    pathname.startsWith("/timeline-approvals/") ||
    pathname === "/project-initiation" ||
    pathname.startsWith("/project-initiation/")
  );
}

function isMastersPath(pathname: string) {
  return (
    pathname === "/geo-directory" ||
    pathname.startsWith("/geo-directory/") ||
    pathname === "/subcontractor-onboarding" ||
    pathname.startsWith("/subcontractor-onboarding/") ||
    pathname === "/engineer-onboarding" ||
    pathname.startsWith("/engineer-onboarding/") ||
    pathname === "/agent-master" ||
    pathname === "/milestone-master" ||
    pathname.startsWith("/milestone-master/") ||
    pathname === "/construction-timeline-master" ||
    pathname.startsWith("/construction-timeline-master/") ||
    pathname === "/standard-resources-master" ||
    pathname.startsWith("/standard-resources-master/") ||
    pathname === "/zones" ||
    pathname.startsWith("/zones/") ||
    pathname === "/inventory/stores-yards" ||
    pathname.startsWith("/inventory/stores-yards/")
  );
}

function isProcurementPath(pathname: string) {
  return (
    pathname === "/inventory/po-format" ||
    pathname.startsWith("/inventory/po-format/") ||
    pathname === "/inventory/po-register" ||
    pathname.startsWith("/inventory/po-register/") ||
    pathname === "/inventory/issue-material" ||
    pathname.startsWith("/inventory/issue-material/")
  );
}

function isInventoryNavPath(pathname: string) {
  return (
    pathname === "/inventory/inward" ||
    pathname.startsWith("/inventory/inward/") ||
    pathname === "/inventory/outward" ||
    pathname.startsWith("/inventory/outward/") ||
    pathname === "/inventory/store-stocks" ||
    pathname.startsWith("/inventory/store-stocks/") ||
    pathname === "/inventory/store-audit" ||
    pathname.startsWith("/inventory/store-audit/")
  );
}

function isInvoicePath(pathname: string) {
  return (
    pathname === "/bills/inward" ||
    pathname.startsWith("/bills/inward/") ||
    pathname === "/bills/labour-suppliers" ||
    pathname.startsWith("/bills/labour-suppliers/")
  );
}

function isExecutionPath(pathname: string) {
  if (pathname === "/milestones" || pathname.startsWith("/milestones/")) return true;
  if (pathname === "/bills/outward" || pathname.startsWith("/bills/outward/")) return true;
  if (isInvoicePath(pathname)) return false;
  if (!pathname.startsWith("/inventory/")) return false;
  if (isProcurementPath(pathname)) return false;
  if (isInventoryNavPath(pathname)) return false;
  if (pathname === "/inventory/stores-yards" || pathname.startsWith("/inventory/stores-yards/")) return false;
  return true;
}

function isAdministrationPath(pathname: string) {
  return (
    pathname === "/console-users" ||
    pathname.startsWith("/console-users/") ||
    pathname === "/audit-log" ||
    pathname.startsWith("/audit-log/")
  );
}

const SHELL_MOBILE_MQ = "(max-width: 900px)";

function shellMobileBreakpointMatches() {
  return typeof window !== "undefined" && window.matchMedia(SHELL_MOBILE_MQ).matches;
}

type BottomPrimaryItem = {
  to: string;
  end?: boolean;
  label: string;
  Icon: ComponentType<{ className?: string }>;
};

export function Layout() {
  const location = useLocation();
  const { can, user, logout, authRequired } = useAuth();
  const lastLoggedPathRef = useRef<string | null>(null);
  const [shellMobileBp, setShellMobileBp] = useState(shellMobileBreakpointMatches);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);

  const [applicationsHubOpen, setApplicationsHubOpen] = useState(() =>
    isApplicationsHubPath(location.pathname),
  );
  const [mastersOpen, setMastersOpen] = useState(() => isMastersPath(location.pathname));
  const [procurementOpen, setProcurementOpen] = useState(() => isProcurementPath(location.pathname));
  const [inventoryOpen, setInventoryOpen] = useState(() => isInventoryNavPath(location.pathname));
  const [projectsOpen, setProjectsOpen] = useState(() => isProjectsPath(location.pathname));
  const [invoiceOpen, setInvoiceOpen] = useState(() => isInvoicePath(location.pathname));
  const [executionOpen, setExecutionOpen] = useState(() => isExecutionPath(location.pathname));
  const [administrationOpen, setAdministrationOpen] = useState(() => isAdministrationPath(location.pathname));

  useEffect(() => {
    if (isApplicationsHubPath(location.pathname)) {
      setApplicationsHubOpen(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (isMastersPath(location.pathname)) {
      setMastersOpen(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (isProcurementPath(location.pathname)) {
      setProcurementOpen(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (isInventoryNavPath(location.pathname)) {
      setInventoryOpen(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (isProjectsPath(location.pathname)) {
      setProjectsOpen(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (isInvoicePath(location.pathname)) {
      setInvoiceOpen(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (isExecutionPath(location.pathname)) {
      setExecutionOpen(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (isAdministrationPath(location.pathname)) {
      setAdministrationOpen(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    setMoreMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!authRequired || !user) return;
    const path = `${location.pathname}${location.search}`;
    if (lastLoggedPathRef.current === path) return;
    lastLoggedPathRef.current = path;
    void postClientAuditEvent({
      eventType: "page_view",
      path,
      label: path,
    }).catch(() => {});
  }, [location.pathname, location.search, authRequired, user]);

  useEffect(() => {
    if (!authRequired || !user) return;
    return installClientActionAudit();
  }, [authRequired, user]);

  useEffect(() => {
    const mq = window.matchMedia(SHELL_MOBILE_MQ);
    const sync = () => {
      setShellMobileBp(mq.matches);
      if (!mq.matches) setMoreMenuOpen(false);
    };
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    if (!moreMenuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMoreMenuOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [moreMenuOpen]);

  /** Mobile bottom bar: Leads → Applicants → Home → Beneficiaries (+ More). Reports / Work stay in the sheet. */
  const bottomPrimaryItems = useMemo((): BottomPrimaryItem[] => {
    const items: BottomPrimaryItem[] = [];
    if (can("leads") || can("leads_import")) {
      items.push({
        to: can("leads") ? "/leads" : "/leads?import=1",
        label: can("leads") ? "Leads" : "Import",
        Icon: IconLayers,
      });
    }
    if (canAccessApplicationsHub(can)) {
      items.push({ to: "/applications", label: "Applicants", Icon: IconFolder });
    }
    items.push({ to: "/", end: true, label: "Home", Icon: IconHome });
    if (can("applications") || canAccessApplicationsHub(can)) {
      items.push({ to: "/subcontractor-to-beneficiary", label: "Beneficiaries", Icon: IconBriefcase });
    }
    return items;
  }, [can]);

  const shellNavProps = {
    can,
    authRequired,
    user,
    applicationsHubOpen,
    setApplicationsHubOpen,
    mastersOpen,
    setMastersOpen,
    procurementOpen,
    setProcurementOpen,
    inventoryOpen,
    setInventoryOpen,
    projectsOpen,
    setProjectsOpen,
    invoiceOpen,
    setInvoiceOpen,
    executionOpen,
    setExecutionOpen,
    administrationOpen,
    setAdministrationOpen,
  };

  return (
    <div className="app-shell app-shell--tg">
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <div className="tg-strip" aria-hidden="true" />
      <header className="topbar topbar--tg">
        <div className="topbar__left topbar__left--brand">
          <div className="topbar__brand topbar__brand--indiramma">
            <img
              src={indirammaLogoSrc}
              alt="Indiramma Indlu — Government of Telangana, Chief Minister’s Office. Beneficiary monitoring and HO console."
              className="topbar__brand-img topbar__brand-img--indiramma"
              width={520}
              height={120}
            />
          </div>
        </div>
        {authRequired && user ? (
          <div className="topbar__right topbar__right--session" style={{ display: "flex", alignItems: "center" }}>
            <ThemeToggle />
            <TopbarAccountMenu displayName={user.displayName || user.email} onLogout={() => logout()} />
          </div>
        ) : null}
      </header>

      <div className="app-body">
        <div className="app-body__row">
        {!shellMobileBp ? (
          <aside id="shell-sidebar-nav" className="sidebar sidebar--tg" aria-label="Main navigation">
            <ShellNavLinks idPrefix="desktop" {...shellNavProps} />
          </aside>
        ) : null}

        {shellMobileBp ? (
          <>
            <nav className="shell-bottom-nav" aria-label="Primary">
              {bottomPrimaryItems.map(({ to, end, label, Icon }) => (
                <NavLink
                  key={to + (end ? "?end" : "")}
                  to={to}
                  end={Boolean(end)}
                  className={bottomNavClass}
                  title={label}
                >
                  <Icon className="shell-bottom-nav__icon" />
                  <span className="shell-bottom-nav__label">{label}</span>
                </NavLink>
              ))}
              <button
                type="button"
                className={"shell-bottom-nav__more" + (moreMenuOpen ? " shell-bottom-nav__more--open" : "")}
                aria-haspopup="dialog"
                aria-expanded={moreMenuOpen}
                onClick={() => setMoreMenuOpen((o) => !o)}
              >
                <IconMenu className="shell-bottom-nav__icon" />
                <span className="shell-bottom-nav__label">More</span>
              </button>
            </nav>
            {moreMenuOpen ? (
              <>
                <button
                  type="button"
                  className="shell-more-backdrop"
                  aria-label="Close menu"
                  onClick={() => setMoreMenuOpen(false)}
                />
                <div
                  className="shell-more-panel"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="shell-more-menu-title"
                >
                  <div className="shell-more-panel__head">
                    <h2 id="shell-more-menu-title" className="shell-more-panel__title">
                      All sections
                    </h2>
                    <button
                      type="button"
                      className="shell-more-panel__close"
                      onClick={() => setMoreMenuOpen(false)}
                      aria-label="Close menu"
                    >
                      <IconClose className="shell-more-panel__close-icon" />
                    </button>
                  </div>
                  <div className="shell-more-panel__body">
                    <aside className="sidebar sidebar--tg sidebar--sheet" aria-label="Full menu">
                      <ShellNavLinks idPrefix="sheet" {...shellNavProps} />
                    </aside>
                  </div>
                </div>
              </>
            ) : null}
          </>
        ) : null}

        <main
          className={"main" + (shellMobileBp ? " main--has-bottom-nav" : "")}
          id="main-content"
          tabIndex={-1}
        >
          <div className="main__scroll">
            <div className="main__scroll-fill">
              <div className="main__canvas">
                <div className="main__inner main__inner--wide">
                  <Outlet />
                </div>
              </div>
              <AppFooter variant="shell" />
            </div>
          </div>
        </main>
        </div>
      </div>
    </div>
  );
}

function ThemeToggle() {
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem("app-theme") || "light";
  });

  useEffect(() => {
    if (theme === "dark") {
      document.documentElement.setAttribute("data-theme", "dark");
      document.documentElement.classList.add("dark-mode");
    } else {
      document.documentElement.removeAttribute("data-theme");
      document.documentElement.classList.remove("dark-mode");
    }
    localStorage.setItem("app-theme", theme);
  }, [theme]);

  return (
    <>
      <style>
        {`
          html.dark-mode {
            filter: invert(1) hue-rotate(180deg);
            background: #111827;
          }
          /* Double-invert images and natively dark components to keep original colors */
          html.dark-mode img,
          html.dark-mode video,
          html.dark-mode iframe,
          html.dark-mode canvas,
          html.dark-mode .dash-key-metrics {
            filter: invert(1) hue-rotate(180deg);
          }
          
          /* Key Metrics specific Dark Mode overrides */
          html.dark-mode .dash-key-metrics__sunburst {
            background: radial-gradient(circle at 50% 50%, #1e293b 0%, #0f172a 100%) !important;
          }
          html.dark-mode .dash-key-metrics__rays {
            opacity: 0.03 !important;
          }
          html.dark-mode .dash-key-metrics__card {
            background: #1e293b !important;
            box-shadow: 0 8px 30px rgba(0,0,0,0.5) !important;
            border: 1px solid #334155 !important;
          }
          html.dark-mode .dash-key-metrics__value {
            color: #f8fafc !important;
          }
          html.dark-mode .dash-key-metrics__label {
            color: #94a3b8 !important;
          }
          html.dark-mode .dash-key-metrics__ribbon {
            background: #334155 !important;
            color: #f1f5f9 !important;
          }

          /* Ensure map tooltips and cards stay readable by letting them invert naturally */
          html.dark-mode .dash-corp-map__panel {
            border-color: #374151;
          }

          /* Fix specific colors that look off after invert */
          html.dark-mode .btn--primary {
            filter: brightness(1.2);
          }
          
          /* Smooth transition for body */
          body {
            transition: background-color 0.3s;
          }
        `}
      </style>
      <button
        type="button"
        onClick={() => setTheme(t => t === "light" ? "dark" : "light")}
        style={{
          background: "transparent",
          border: "none",
          cursor: "pointer",
          padding: "0.5rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "50%",
          color: "#14b8a6", // matching the user profile pill color vibe
          marginRight: "0.5rem",
          transition: "background 0.2s"
        }}
        onMouseEnter={(e) => e.currentTarget.style.background = "rgba(20, 184, 166, 0.1)"}
        onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
        title={`Switch to ${theme === "light" ? "Dark" : "Light"} Mode`}
      >
        {theme === "light" ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path></svg>
        ) : (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"></circle><line x1="12" y1="1" x2="12" y2="3"></line><line x1="12" y1="21" x2="12" y2="23"></line><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line><line x1="1" y1="12" x2="3" y2="12"></line><line x1="21" y1="12" x2="23" y2="12"></line><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line></svg>
        )}
      </button>
    </>
  );
}

function TopbarAccountMenu({ displayName, onLogout }: { displayName: string; onLogout: () => void }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [resetPwOpen, setResetPwOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const el = wrapRef.current;
      if (el && !el.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  return (
    <>
      <div className="topbar__account" ref={wrapRef}>
        <button
          type="button"
          className="topbar__account-trigger"
          id="topbar-account-trigger"
          aria-expanded={menuOpen}
          aria-haspopup="menu"
          aria-controls="topbar-account-menu"
          onClick={() => setMenuOpen((o) => !o)}
        >
          <span className="topbar__account-name">{displayName}</span>
          <IconChevronDown className="topbar__account-chevron" aria-hidden />
        </button>
        {menuOpen ? (
          <div
            id="topbar-account-menu"
            className="topbar__account-dropdown"
            role="menu"
            aria-labelledby="topbar-account-trigger"
          >
            <button
              type="button"
              role="menuitem"
              className="topbar__account-item"
              onClick={() => {
                setMenuOpen(false);
                setResetPwOpen(true);
              }}
            >
              Reset password
            </button>
            <button
              type="button"
              role="menuitem"
              className="topbar__account-item"
              onClick={() => {
                setMenuOpen(false);
                onLogout();
              }}
            >
              Logout
            </button>
          </div>
        ) : null}
      </div>
      {resetPwOpen ? <ResetPasswordModal onClose={() => setResetPwOpen(false)} /> : null}
    </>
  );
}

function ResetPasswordModal({ onClose }: { onClose: () => void }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setError(null);
    if (newPassword !== confirmPassword) {
      setError("New passwords do not match.");
      return;
    }
    if (newPassword.length < 8) {
      setError("New password must be at least 8 characters.");
      return;
    }
    setBusy(true);
    try {
      await changeConsolePassword(currentPassword, newPassword);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="reset-pw-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="reset-pw-title">Reset password</h2>
        <p className="modal__lede">
          Enter your current password and choose a new one (at least 8 characters).
        </p>
        {error ? (
          <p className="text-warning" style={{ marginBottom: "0.75rem", fontSize: "0.875rem" }}>
            {error}
          </p>
        ) : null}
        <div className="field-grid">
          <div className="field">
            <label htmlFor="reset-pw-current">Current password</label>
            <input
              id="reset-pw-current"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              disabled={busy}
            />
          </div>
          <div className="field">
            <label htmlFor="reset-pw-new">New password</label>
            <input
              id="reset-pw-new"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={busy}
              minLength={8}
            />
          </div>
          <div className="field">
            <label htmlFor="reset-pw-confirm">Confirm new password</label>
            <input
              id="reset-pw-confirm"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={busy}
              minLength={8}
            />
          </div>
        </div>
        <div className="modal__actions">
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn btn--primary btn--tg" onClick={() => void submit()} disabled={busy}>
            {busy ? "Saving…" : "Save password"}
          </button>
        </div>
      </div>
    </div>
  );
}

function bottomNavClass({ isActive }: { isActive: boolean }) {
  return "shell-bottom-nav__link" + (isActive ? " shell-bottom-nav__link--active" : "");
}
